import React, { useEffect, useMemo, useState } from "react";
import "./Gallery.css";
import Footer from "../components/Footer";
import { fetchAndCachePublicData, getCachedPublicData } from "../utils/publicDataCache";

const API = "/backend/public";
const GALLERY_CACHE_KEY = "public-gallery-occasions";

function Gallery() {
  const [occasions, setOccasions] = useState([]);
  const [activeOccasionId, setActiveOccasionId] = useState(null);
  const [viewer, setViewer] = useState({ occasionId: null, photoIndex: 0 });

  useEffect(() => {
    let active = true;

    const loadGallery = async () => {
      const cached = getCachedPublicData(GALLERY_CACHE_KEY);
      if (cached && active) {
        setOccasions(cached.data);
      }

      try {
        const rows = await fetchAndCachePublicData(GALLERY_CACHE_KEY, "/gallery-occasions");

        if (!active) return;

        setOccasions(rows);
        setActiveOccasionId(null);
      } catch {
        if (!active) return;
        if (!cached) {
          setOccasions([]);
          setActiveOccasionId(null);
        }
      }
    };

    loadGallery();

    return () => {
      active = false;
    };
  }, []);

  const activeOccasion = useMemo(
    () => occasions.find((item) => item.id === activeOccasionId) || null,
    [activeOccasionId, occasions]
  );

  const viewerOccasion = useMemo(
    () => occasions.find((item) => item.id === viewer.occasionId) || null,
    [occasions, viewer.occasionId]
  );

  const viewerPhoto = viewerOccasion?.photos?.[viewer.photoIndex] || null;

  const openViewer = (occasionId, photoIndex) => {
    setViewer({ occasionId, photoIndex });
    document.body.style.overflow = "hidden";
  };

  const closeViewer = () => {
    setViewer({ occasionId: null, photoIndex: 0 });
    document.body.style.overflow = "";
  };

  const moveViewer = (direction) => {
    if (!viewerOccasion?.photos?.length) return;

    const total = viewerOccasion.photos.length;
    const nextIndex = (viewer.photoIndex + direction + total) % total;
    setViewer((prev) => ({ ...prev, photoIndex: nextIndex }));
  };

  useEffect(() => {
    if (!viewerOccasion) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setViewer({ occasionId: null, photoIndex: 0 });
        document.body.style.overflow = "";
      }

      if (event.key === "ArrowLeft") {
        setViewer((prev) => ({
          ...prev,
          photoIndex: (prev.photoIndex - 1 + viewerOccasion.photos.length) % viewerOccasion.photos.length,
        }));
      }

      if (event.key === "ArrowRight") {
        setViewer((prev) => ({
          ...prev,
          photoIndex: (prev.photoIndex + 1) % viewerOccasion.photos.length,
        }));
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [viewerOccasion, viewer.photoIndex]);

  return (
    <>
      <div className="gallery-page">
        <div className="gallery-shell container py-5">
          <div className="gallery-hero">
            <span className="gallery-kicker">Memories & Moments</span>
            <h1>Gallery</h1>
          </div>

          {!activeOccasion ? (
            <div className="gallery-occasion-list">
              {occasions.map((occasion) => (
                <button
                  key={occasion.id}
                  type="button"
                  className="gallery-occasion-card"
                  onClick={() => setActiveOccasionId(occasion.id)}
                >
                  <div className="gallery-occasion-thumb">
                    <img src={`${API}/uploads/${occasion.thumbnail}`} alt={occasion.title} />
                  </div>
                  <div className="gallery-occasion-copy">
                    <h3>{occasion.title}</h3>
                  </div>
                </button>
              ))}
            </div>
          ) : null}

          {activeOccasion ? (
            <div className="gallery-album-panel">
              <div className="gallery-album-heading">
                <div>
                  <h2>{activeOccasion.title}</h2>
                  <p>{activeOccasion.description || "No description available for this occasion yet."}</p>
                </div>
                <div className="gallery-album-actions">
                  <div className="gallery-album-count">{activeOccasion.photos?.length || 0} images</div>
                  <button type="button" className="gallery-back-button" onClick={() => setActiveOccasionId(null)}>
                    Back To Gallery
                  </button>
                </div>
              </div>

              <div className="gallery-grid">
                {(activeOccasion.photos || []).map((photo, index) => (
                  <button key={photo.id} type="button" className="gallery-item" onClick={() => openViewer(activeOccasion.id, index)}>
                    <img src={`${API}/uploads/${photo.image}`} alt={photo.title || activeOccasion.title} />
                    <div className="gallery-item-overlay">
                      <strong>{photo.title || activeOccasion.title}</strong>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : !occasions.length ? (
            <div className="gallery-empty">
              No gallery occasion available right now.
            </div>
          ) : null}
        </div>
      </div>

      {viewerPhoto ? (
        <div className="gallery-lightbox" onClick={closeViewer}>
          <button type="button" className="gallery-lightbox-close" onClick={closeViewer}>
            Close
          </button>
          <button
            type="button"
            className="gallery-lightbox-nav gallery-lightbox-prev"
            onClick={(event) => {
              event.stopPropagation();
              moveViewer(-1);
            }}
          >
            Prev
          </button>
          <div className="gallery-lightbox-content" onClick={(event) => event.stopPropagation()}>
            <img src={`${API}/uploads/${viewerPhoto.image}`} alt={viewerPhoto.title || viewerOccasion.title} />
            <div className="gallery-lightbox-caption">
              <h3>{viewerPhoto.title || viewerOccasion.title}</h3>
              <p>{viewerPhoto.title ? viewerOccasion.title : viewerOccasion.description || "Gallery preview"}</p>
            </div>
          </div>
          <button
            type="button"
            className="gallery-lightbox-nav gallery-lightbox-next"
            onClick={(event) => {
              event.stopPropagation();
              moveViewer(1);
            }}
          >
            Next
          </button>
        </div>
      ) : null}

      <Footer />
    </>
  );
}

export default Gallery;



